import qs from 'qs';
import { API_BASE_URL } from '../config/api';
import {
  accessTokenNeedsRefresh,
  currentAccessToken,
  isExpiredAuthError,
  logoutSession,
  persistTokens,
  refreshSession,
  shouldSkipAuthRefresh,
  subscribeSession,
  type AuthTokens,
} from './core/auth-session';

const API_URL = API_BASE_URL;

export type ListQuery = {
  queryMeta?: {
    page?: number;
    limit?: number;
    paginate?: boolean;
    order?: Record<string, 'ASC' | 'DESC' | 'asc' | 'desc'>;
  };
  filterMeta?: Record<string, unknown>;
  countryCode?: string;
  isActive?: boolean;
  [key: string]: unknown;
};

export interface EsimPlan {
  uuid: string;
  country: string;
  countryCode: string;
  durationDays: number;
  dataAmountGb: number;
  price: number;
  totalPrice?: number;
  currency: string;
  isActive: boolean;
  name?: string;
  /** eSIM Access: 1=fixed, 2/3=daily limit, 4=daily unlimited */
  dataType?: number | null;
  isUnlimited?: boolean;
  fupPolicy?: string | null;
  /** Present on catalog plans from eSIM Access — used for coverage chips. */
  locationNetworkList?: Array<Record<string, unknown>>;
}

export interface CountryBannerImage {
  uuid: string;
  countryCode: string;
  imageUrl: string;
  sortOrder: number;
}

export interface Country {
  uuid: string;
  name: string;
  countryCode: string;
  backgroundImage?: string | null;
  heroImageUrl?: string | null;
  bannerImages?: CountryBannerImage[];
}

export interface EsimOrder {
  uuid: string;
  userId: string;
  planId?: string;
  status: string;
  price: number;
  productPrice?: number;
  serviceFee?: number;
  basePrice?: number;
  commission?: number;
  commissionRate?: number;
  totalPrice?: number;
  currency: string;
  telegramPaymentChargeId?: string;
  packageCode?: string;
  createdAt?: string;
  canCancel?: boolean;
  cancelExpiresAt?: string | null;
  esimId?: string | null;
  iccid?: string | null;
  locationCode?: string | null;
  countryCode?: string | null;
  country?: string | null;
  packageName?: string | null;
}

export interface Esim {
  uuid: string;
  orderId: string;
  iccid?: string;
  activationCode?: string;
  qrCodeUrl?: string;
  smdpAddress?: string;
  status: string;
  expiresAt?: string;
  dataUsedMb?: number;
  dataTotalMb?: number;
  packageName?: string;
  locationCode?: string;
  volume?: number;
  volumeUsage?: number;
  active?: boolean;
  activeAt?: string;
  duration?: number;
  esimTranNo?: string;
  usageUpdatedAt?: string;
  /** Real-time network attachment, independent of tariff `active`/`status`. */
  connectionStatus?: 'connected' | 'not_connected' | 'unknown';
  /** dataUsedMb / dataTotalMb * 100, computed server-side; null until usage has ever arrived. */
  usagePercent?: number | null;
  createdAt?: string;
  canCancel?: boolean;
  cancelExpiresAt?: string | null;
}

export interface PromoQuote {
  originalAmount: number;
  discountPercent: number;
  discountAmount: number;
  finalAmount: number;
  code: string;
  remaining: number;
}

export interface UserOrder {
  uuid: string;
  userId: string;
  txId?: string;
  success: boolean;
  orderNumber?: string;
  iccid?: string;
  duration?: number;
  volume?: number;
  volumeUsage?: number;
  locationCode?: string;
  packageName?: string;
  packageCode?: string;
  planId?: string;
  qrCodeUrl?: string;
  smdpAddress?: string;
  activeAt?: string;
  active: boolean;
  esimId?: string;
  price?: number;
  currency?: string;
  status?: string;
  createdAt?: string;
  canCancel?: boolean;
  cancelExpiresAt?: string | null;
}

export interface AppUser {
  uuid: string;
  tg_id?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  language_code?: string;
  balance: number;
  orders: UserOrder[];
  payments?: string[];
  avatarUrl?: string | null;
  createdAt?: string;
  referralCode?: string;
  referralEarningsUsd?: number;
  emailVerifiedAt?: string | null;
  emailVerified?: boolean;
}

export interface Referral {
  name?: string;
  joinedAt?: string;
}

export interface ReferralsResult {
  referralCode?: string;
  referralEarningsUsd: number;
  count: number;
  referrals: Referral[];
}

export interface PaymentMethod {
  key: 'telegram' | 'stripe' | 'apple_pay' | 'google_pay';
  label: string;
  enabled: boolean;
}

export interface PaymentSessionStatus {
  status: 'pending' | 'succeeded' | 'failed';
  amountUsd: number;
  basePrice?: number;
  commission?: number;
  totalPrice?: number;
  newBalance?: number;
  balance: number;
}

export interface PaymentTransaction {
  uuid: string;
  userId: string;
  provider: string;
  providerRef?: string;
  amountUsd: number;
  basePrice?: number;
  commission?: number;
  commissionRate?: number;
  totalPrice?: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'cancelled';
  createdAt?: string;
}

export interface PaymentPreview {
  type: 'topup' | 'package';
  totalPrice: number;
  currency: string;
  originalAmount?: number;
  discountAmount?: number;
  promoCode?: string;
}

export interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface AppNotification {
  uuid: string;
  userId: string;
  type: 'low_data' | 'expiry' | 'esim_ready' | string;
  title: string;
  body: string;
  esimId?: string;
  readAt?: string;
  createdAt?: string;
}

export interface ChatReaction {
  uuid: string;
  messageId: string;
  userId: string;
  emoji: string;
}

export interface ChatReplyPreview {
  uuid: string;
  kind: 'text' | 'image' | string;
  body?: string | null;
  imageUrl?: string | null;
  senderRole: 'user' | 'admin' | string;
}

export interface ChatMessage {
  uuid: string;
  dialogId: string;
  senderId: string;
  senderRole: 'user' | 'admin' | string;
  kind: 'text' | 'image' | string;
  body?: string | null;
  imageUrl?: string | null;
  createdAt?: string;
  reactions?: ChatReaction[];
  replyToId?: string | null;
  replyTo?: ChatReplyPreview | null;
}

export interface ChatDialog {
  uuid: string;
  type: string;
  userId: string;
  status: string;
  lastMessageAt?: string | null;
  lastMessagePreview?: string | null;
  userUnreadCount: number;
  adminUnreadCount: number;
  userLastReadAt?: string | null;
  adminLastReadAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupportChatPayload {
  dialog: ChatDialog;
  messages: ChatMessage[];
}

export interface ListResult<T> {
  data: T[];
  meta?: { count: number; page: number; limit: number };
}

export interface CatalogCountry {
  country: string;
  countryCode: string;
  minPrice: number;
  currency: string;
  planCount: number;
  backgroundImage?: string | null;
}

export class ApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, body: string) {
    super(body || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    subscribeSession((event) => {
      if (event.type === 'refreshed') this.token = event.accessToken;
      if (event.type === 'cleared') this.token = null;
    });
  }

  setToken(token: string) {
    this.token = token;
  }

  setTokens(tokens: AuthTokens) {
    this.token = tokens.access_token;
    persistTokens(tokens);
  }

  private async sendResponse(path: string, options: RequestInit = {}, retried = false): Promise<Response> {
    const skipRefresh = shouldSkipAuthRefresh(path);
    if (!skipRefresh && !retried && accessTokenNeedsRefresh(this.token)) {
      const refreshed = await refreshSession();
      if (refreshed) this.token = currentAccessToken();
      else this.dropSession();
    }

    const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
    if (this.token && !skipRefresh) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const res = await fetch(`${API_URL}${path}`, { ...options, headers, cache: 'no-store' });
    if (!res.ok) {
      const text = await res.text();
      if (!skipRefresh && !retried && isExpiredAuthError(res.status, text)) {
        const refreshed = await refreshSession();
        if (refreshed) {
          this.token = currentAccessToken();
          return this.sendResponse(path, options, true);
        }
        this.dropSession();
        return this.sendResponse(path, options, true);
      }
      throw new ApiError(res.status, text || `HTTP ${res.status}`);
    }
    return res;
  }

  private async send<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await this.sendResponse(path, options);
    if (res.status === 204) return undefined as T;
    return res.json();
  }

  private async sendBlob(path: string, options: RequestInit = {}): Promise<Blob> {
    const res = await this.sendResponse(path, options);
    return res.blob();
  }

  private dropSession() {
    logoutSession();
    this.token = currentAccessToken();
  }

  private request<T>(path: string, options: RequestInit = {}): Promise<T> {
    return this.send<T>(path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      },
    });
  }

  login(initData: string, referralCode?: string) {
    return this.request<{ access_token: string; refresh_token?: string }>('/api/v1/telegram-auth/login', {
      method: 'POST',
      body: JSON.stringify({ initData, referralCode }),
    });
  }

  loginEmail(email: string, password: string) {
    return this.request<{ access_token: string; refresh_token?: string }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  registerEmail(input: { email: string; password: string; name?: string; phone?: string; referralCode?: string }) {
    return this.request<{ access_token: string; refresh_token?: string }>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  loginGoogle(idToken: string, referralCode?: string) {
    return this.request<{ access_token: string; refresh_token?: string }>('/api/v1/google-auth/login', {
      method: 'POST',
      body: JSON.stringify({ idToken, referralCode }),
    });
  }

  loginTelegramWidget(payload: {
    id: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
    referralCode?: string;
  }) {
    return this.request<{ access_token: string; refresh_token?: string }>('/api/v1/telegram-auth/login-widget', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  forgotPassword(email: string) {
    return this.request<{ sent: boolean }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  resetPassword(token: string, password: string) {
    return this.request<{ ok: boolean }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  verifyEmail(token: string) {
    const q = `?token=${encodeURIComponent(token)}`;
    return this.request<{ ok: boolean; alreadyVerified?: boolean }>(`/api/v1/auth/verify-email${q}`);
  }

  resendVerification(email: string) {
    return this.request<{ sent: boolean }>('/api/v1/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  checkContact(input: { email?: string; phone?: string; type: string }) {
    return this.request<{ sent: boolean }>('/api/v1/auth/checkContact', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  verifyContact(input: { email?: string; phone?: string; code: string; type: string }) {
    return this.request<{ verified: boolean }>('/api/v1/auth/verifyContact', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  getPaymentMethods() {
    return this.request<PaymentMethod[]>('/api/v1/payment-methods');
  }

  createStripeCheckout(amount: number) {
    return this.request<{ url: string; sessionId: string; pricing?: PaymentPreview }>(
      '/api/v1/payment/stripe/checkout',
      {
        method: 'POST',
        body: JSON.stringify({ amount }),
      },
    );
  }

  /** PaymentIntent for in-app card Elements and external Apple/Google Pay (/pay). */
  createStripeWalletIntent(amount: number, opts?: { walletsOnly?: boolean }) {
    return this.request<{
      clientSecret: string;
      transactionUuid: string;
      publishableKey: string;
      pricing?: PaymentPreview;
    }>('/api/v1/payment/stripe/wallet-intent', {
      method: 'POST',
      body: JSON.stringify({
        amount,
        ...(opts?.walletsOnly ? { walletsOnly: true } : {}),
      }),
    });
  }

  getPaymentStatus(sessionId: string) {
    const query = this.toQuery({ sessionId });
    return this.request<PaymentSessionStatus>(`/api/v1/payment/status${query}`);
  }

  createStripeSetupSession() {
    return this.request<{ url: string }>('/api/v1/payment/stripe/setup', { method: 'POST' });
  }

  getSavedCards() {
    return this.request<SavedCard[]>('/api/v1/payment/methods/cards');
  }

  deleteSavedCard(id: string) {
    return this.request<{ ok: boolean }>(`/api/v1/payment/methods/cards/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  chargeSavedCard(id: string, amount: number) {
    return this.request<{ status: string; pricing?: PaymentPreview }>(
      `/api/v1/payment/methods/cards/${encodeURIComponent(id)}/charge`,
      {
        method: 'POST',
        body: JSON.stringify({ amount }),
      },
    );
  }

  /** Server-side fee quote — display only; create-payment recalculates. */
  previewPayment(input: { type: 'topup'; amount: number } | { type: 'package'; packageCode: string }) {
    const query =
      input.type === 'topup'
        ? this.toQuery({ type: 'topup', amount: input.amount })
        : this.toQuery({ type: 'package', packageCode: input.packageCode });
    return this.request<PaymentPreview>(`/api/v1/payment/preview${query}`);
  }

  getPaymentTransactions() {
    const query = this.toQuery({ queryMeta: { paginate: true, page: 1, order: { createdAt: 'DESC' }, limit: 50 } });
    return this.request<ListResult<PaymentTransaction>>(`/api/v1/payment/transactions${query}`);
  }

  getNotifications() {
    const query = this.toQuery({ queryMeta: { paginate: true, page: 1, order: { createdAt: 'DESC' }, limit: 50 } });
    return this.request<ListResult<AppNotification>>(`/api/v1/notification${query}`);
  }

  markNotificationRead(id: string) {
    return this.request<AppNotification>(`/api/v1/notification/${encodeURIComponent(id)}/read`, {
      method: 'PATCH',
    });
  }

  getSupportChat(limit = 50) {
    const query = this.toQuery({ limit });
    return this.request<SupportChatPayload>(`/api/v1/chat/support${query}`);
  }

  getPublicSupportContacts() {
    return this.request<{ supportEmail: string; supportTelegram: string }>(
      '/api/v1/configuration/public-contacts',
    );
  }

  createSupportChat() {
    return this.request<ChatDialog>('/api/v1/chat/support', { method: 'POST' });
  }

  getChatMessages(dialogId: string, params?: { before?: string; limit?: number }) {
    const query = this.toQuery({
      before: params?.before,
      limit: params?.limit ?? 50,
    });
    return this.request<{ data: ChatMessage[] }>(
      `/api/v1/chat/${encodeURIComponent(dialogId)}/messages${query}`,
    );
  }

  sendChatMessage(
    dialogId: string,
    input: { kind: 'text' | 'image'; body?: string; imageUrl?: string; replyToId?: string },
  ) {
    return this.request<ChatMessage>(`/api/v1/chat/${encodeURIComponent(dialogId)}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  markChatRead(dialogId: string) {
    return this.request<ChatDialog>(`/api/v1/chat/${encodeURIComponent(dialogId)}/read`, {
      method: 'POST',
    });
  }

  async uploadChatImage(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.send<{ url: string; key: string }>('/api/v1/chat/upload', {
      method: 'POST',
      body: form,
    });
  }

  clearToken() {
    this.token = null;
  }

  private toQuery(params?: ListQuery) {
    if (!params) return '';
    const query = qs.stringify(params, {
      encode: true,
      skipNulls: true,
      allowDots: false,
      arrayFormat: 'brackets',
    });
    return query ? `?${query}` : '';
  }

  getPlans(params?: ListQuery) {
    const query = this.toQuery({
      ...params,
      queryMeta: {
        paginate: true,
        page: 1,
        order: { countryCode: 'ASC', priceUsd: 'ASC' },
        ...(params?.queryMeta || {}),
        limit: Math.min(Number(params?.queryMeta?.limit) || 20, 20),
      },
    });
    return this.request<ListResult<EsimPlan>>(`/api/v1/esim-plan${query}`);
  }

  getPlanCountries(params?: ListQuery, opts?: { signal?: AbortSignal }) {
    const query = this.toQuery({
      ...params,
      queryMeta: {
        paginate: true,
        page: 1,
        order: { countryCode: 'ASC' },
        ...(params?.queryMeta || {}),
        limit: Math.min(Number(params?.queryMeta?.limit) || 20, 20),
      },
    });
    return this.request<ListResult<CatalogCountry>>(`/api/v1/esim-plan/countries${query}`, {
      signal: opts?.signal,
    });
  }

  getPlan(id: string) {
    return this.request<EsimPlan>(`/api/v1/esim-plan/${encodeURIComponent(id)}`);
  }

  getAllTariffs() {
    return this.request<Array<Record<string, unknown>>>('/api/v1/tariffs/getAllTariffs');
  }

  orderEsim(packageCode: string, price: number, count = 1, promoCode?: string, periodNum?: number) {
    return this.request<UserOrder>('/api/v1/tariffs/orderTariff', {
      method: 'POST',
      body: JSON.stringify({
        packageCode,
        price,
        count,
        ...(promoCode ? { promoCode } : {}),
        ...(periodNum != null ? { periodNum } : {}),
      }),
    });
  }

  validatePromoCode(input: { code: string; amount?: number; packageCode?: string }) {
    return this.request<PromoQuote>('/api/v1/promocodes/validate', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  getMe() {
    return this.request<AppUser>('/api/v1/user/getUser');
  }

  updateMe(input: { name?: string; surname?: string }) {
    return this.request<AppUser>('/api/v1/user/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async uploadMyAvatar(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.send<AppUser>('/api/v1/user/me/avatar', {
      method: 'POST',
      body: form,
    });
  }

  getReferrals() {
    return this.request<ReferralsResult>('/api/v1/user/referrals');
  }

  getCountry(code: string) {
    return this.request<Country>(`/api/v1/country/${code}`);
  }

  getCountryImageCatalog() {
    return this.request<Country[]>('/api/v1/country/catalog/images');
  }

  createOrder(planId: string) {
    return this.request<EsimOrder>('/api/v1/esim-order', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }

  getOrder(id: string) {
    return this.request<EsimOrder>(`/api/v1/esim-order/${id}`);
  }

  getMyOrders(params?: ListQuery) {
    const query = this.toQuery({
      queryMeta: {
        paginate: true,
        page: 1,
        order: { createdAt: 'DESC' },
        ...(params?.queryMeta || {}),
        limit: Math.min(Number(params?.queryMeta?.limit) || 50, 50),
      },
    });
    return this.request<ListResult<EsimOrder>>(`/api/v1/esim-order/mine${query}`);
  }

  confirmPayment(orderId: string, telegramPaymentChargeId: string) {
    return this.request<EsimOrder>(`/api/v1/esim-order/${orderId}/pay`, {
      method: 'PATCH',
      body: JSON.stringify({ telegramPaymentChargeId }),
    });
  }

  retryProvisionOrder(orderId: string) {
    return this.request<EsimOrder>(`/api/v1/esim-order/${orderId}/retry`, { method: 'POST' });
  }

  cancelOrder(orderId: string) {
    return this.request<{ ok: boolean; status: string; orderId: string }>(
      `/api/v1/esim-order/${orderId}/cancel`,
      { method: 'POST' },
    );
  }

  getEsims(params?: ListQuery) {
    const query = this.toQuery({
      queryMeta: {
        paginate: true,
        page: 1,
        ...(params?.queryMeta || {}),
        limit: Math.min(Number(params?.queryMeta?.limit) || 20, 20),
      },
      filterMeta: params?.filterMeta,
    });
    return this.request<ListResult<Esim>>(`/api/v1/esim${query}`);
  }

  getEsim(id: string) {
    return this.request<Esim>(`/api/v1/esim/${encodeURIComponent(id)}`);
  }

  getQrcodePng(data: string, width?: number) {
    const params = new URLSearchParams({ data });
    if (width) params.set('width', String(width));
    return this.sendBlob(`/api/v1/qrcode?${params.toString()}`);
  }

  getEsimQrcodePng(id: string, width?: number) {
    const params = width ? `?width=${encodeURIComponent(String(width))}` : '';
    return this.sendBlob(`/api/v1/esim/${encodeURIComponent(id)}/qrcode${params}`);
  }

  async getOrdersFromEsims(): Promise<EsimOrder[]> {
    const esims = await this.getEsims({ queryMeta: { page: 1, limit: 20, paginate: true } });
    const orderIds = [...new Set((esims.data || []).map((e) => e.orderId).filter(Boolean))];
    const orders = await Promise.all(orderIds.map((id) => this.getOrder(id).catch(() => null)));
    return orders.filter((o): o is EsimOrder => o !== null);
  }
}

export const api = new ApiClient();
